import { useMemo } from "react";

export const OS = Object.freeze({
    ANDROID: "Android",
    IOS: "iOS",
    WINDOWS: "Windows",
});

const ANDROID_RE = /Android/i;
const IOS_RE = /iPhone|iPad|iPod/i;
const WINDOWS_RE = /Windows NT|Win32|Win64|Windows|WinCE/i;

let __cachedOS;

const isIpadOS = () => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    const touch = navigator.maxTouchPoints || 0;
    return /iPad/i.test(ua) || (/Mac/i.test(ua) && touch > 1);
};

export function detectOS() {
    if (__cachedOS !== undefined) return __cachedOS;
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        __cachedOS = null;
        return __cachedOS;
    }
    const uaData = navigator.userAgentData;
    if (uaData?.platform) {
        const p = uaData.platform.toLowerCase();
        if (p.includes("android")) __cachedOS = OS.ANDROID;
        else if (p.includes("ios") || p.includes("iphone") || p.includes("ipad") ||  p.includes("mac")) __cachedOS = OS.IOS;
        else if (p.includes("win")) __cachedOS = OS.WINDOWS;
    }
    if (__cachedOS === undefined) {
        const ua = navigator.userAgent;
        if (ANDROID_RE.test(ua)) __cachedOS = OS.ANDROID;
        else if (IOS_RE.test(ua) || isIpadOS() || /Mac/i.test(ua)) __cachedOS = OS.IOS;
        else if (WINDOWS_RE.test(ua)) __cachedOS = OS.WINDOWS;
        else __cachedOS = null;
    }
    return __cachedOS;
}

export const isMobileOS = (os) => os === OS.ANDROID || os === OS.IOS;

export function useUserAgent() {
    return useMemo(() => detectOS(), []);
}

/* ───────── 런타임/브릿지 감지 추가 ───────── */
export const Runtime = Object.freeze({
    WEB: "web",
    ANDROID_WEBVIEW: "android-webview",
    IOS_WEBVIEW: "ios-webview",
    RN_WEBVIEW: "rn-webview",
    PWA: "pwa",
    SSR: "ssr",
});

export function detectRuntime() {
    if (typeof window === "undefined") return Runtime.SSR;

    // React Native WebView
    if (window.ReactNativeWebView) return Runtime.RN_WEBVIEW;

    // iOS WKWebView (메시지 핸들러)
    const iosHandlers = window.webkit?.messageHandlers;
    if (iosHandlers) {
        // 프로젝트에서 쓰는 채널명 추가
        if (iosHandlers.AppBridge || iosHandlers.callbackHandler) return Runtime.IOS_WEBVIEW;
        // 다른 핸들러라도 하나라도 있으면 iOS WebView일 확률 큼
        if (Object.keys(iosHandlers).length > 0) return Runtime.IOS_WEBVIEW;
    }

    // Android WebView (표준 postMessage 채널 or 레거시 JS 인터페이스)
    const hasAndroidPost = !!window.AppBridge?.postMessage;
    const hasLegacyAndroid =
        !!window.AndroidDeviceInfo ||
        !!window.AndroidToken ||
        !!window.AndroidLocation ||
        !!window.AndroidNotification;

    if (hasAndroidPost || hasLegacyAndroid) return Runtime.ANDROID_WEBVIEW;

    // PWA (독립 실행)
    const isStandalone =
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        (typeof navigator !== "undefined" && navigator.standalone === true);
    if (isStandalone) return Runtime.PWA;

    return Runtime.WEB;
}

export function hasNativeBridge() {
    const r = detectRuntime();
    return (
        r === Runtime.ANDROID_WEBVIEW ||
        r === Runtime.IOS_WEBVIEW ||
        r === Runtime.RN_WEBVIEW
    );
}

export function useRuntime() {
    return useMemo(() => detectRuntime(), []);
}

export default useUserAgent;